import type { ColumnDef } from '@tanstack/vue-table';
import type { ComputedRef, InjectionKey, VNode } from 'vue';

// How a table renders below the breakpoint. 'auto' keeps the table with
// natural widths and sideways scroll (ITDEVELOP-3146); 'cards' opts a page into
// cards-first; 'table' opts out of mobile adaptation entirely. See DataTable's
// prop docs.
export type MobileMode = 'table' | 'cards' | 'auto';

// ITDEVELOP-3170: the user-facing display choice, persisted per table.
export type ViewMode = 'table' | 'cards';

// ITDEVELOP-3251: DataTable provides its effective view so interactive cell
// components can adapt. In card view the whole card is the tap target
// (row-click), so a cell's own click-to-open surface (e.g. the user-info
// popover) must stand down and let the tap bubble to the card.
export const DATA_TABLE_VIEW: InjectionKey<ComputedRef<ViewMode>> = Symbol('data-table-view');

// A column as the display controls present it: identity + current visibility.
// `locked` columns (the row's identity column, action buttons) cannot be
// hidden — hiding them would leave rows unidentifiable or inoperable.
export interface ToggleableColumn {
    id: string;
    label: string;
    visible: boolean;
    locked: boolean;
}

export type SortDirection = 'ascending' | 'descending';

export interface MobileColumn {
    key: string;
    label: string;
}

export interface FilterConfig {
    [key: string]: unknown;
}

export interface EmptyStateConfig {
    icon?: string | false;
    title?: string;
    message?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export interface NoResultsStateConfig {
    icon?: string | false;
    title?: string;
    message?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export interface DataTableProps<T> {
    data: T[];
    columns: ColumnDef<T, unknown>[];
    loading?: boolean;

    // Features
    enableSorting?: boolean;
    enableSearch?: boolean;
    enablePagination?: boolean;
    enableRowExpansion?: boolean;

    // Pagination
    pageSize?: number;
    currentPage?: number;
    totalPages?: number;

    // Mobile support
    mobileMode?: MobileMode;
    mobileColumns?: MobileColumn[];
    mobileBreakpoint?: number; // default 1024px (lg)

    // Custom filters
    filters?: FilterConfig;
    enableFilters?: boolean;

    // Empty states
    emptyStateConfig?: EmptyStateConfig;
    noResultsStateConfig?: NoResultsStateConfig;

    // Search
    searchPlaceholder?: string;
    searchDebounce?: number;

    // Row interaction
    onRowClick?: (row: T) => void;

    // Styling
    tableClass?: string;
    rowClass?: string | ((row: T) => string);

    // Unique key for rows
    rowKey?: keyof T | ((row: T) => string | number);
}

export interface DataTableEmits<T> {
    'row-click': [row: T];
    'sort-change': [column: string, direction: SortDirection];
    'search-change': [query: string];
    'page-change': [page: number];
    'filters-change': [filters: FilterConfig];
}

export interface DataTableSlots<T> {
    'empty-state': () => VNode;
    'no-results': () => VNode;
    'mobile-card': (props: { row: T; index: number }) => VNode;
    'expanded-row': (props: { row: T }) => VNode;
    filters: (props: { filterState: FilterConfig }) => VNode;
    'mobile-filters': (props: { filterState: FilterConfig }) => VNode;
    'search-prepend': () => VNode;
    'search-append': () => VNode;
    'table-header-actions': () => VNode;
}

// Cell renderer prop types
export interface CellProps<T, V = unknown> {
    value: V;
    row: T;
}

// Column factory options
export interface ColumnOptions {
    header?: string;
    enableSorting?: boolean;
    className?: string;
    size?: number;
    minSize?: number;
    maxSize?: number;
}

export type TagVariant =
    | 'default'
    | ''
    | 'primary'
    | 'success'
    | 'success-muted'
    | 'warning'
    | 'danger'
    | 'info'
    | 'hold'
    | 'done'
    | 'wip'
    | 'silver'
    | 'gold'
    | 'bronze'
    | 'gray';
export interface TagCellProps {
    value: string;
    variant?: TagVariant;
    customVariants?: Record<string, TagVariant>;
}

export interface AvatarNameCellProps {
    firstName: string;
    lastName: string;
    size?: 'x-small' | 'small' | 'medium' | 'large';
}

export interface ActionButton {
    label?: string;
    icon?: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger' | 'link';
    disabled?: boolean;
}

export interface ActionButtonsCellProps {
    actions: ActionButton[];
}
